import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { APP_DISPLAY_NAME } from '../config/brand';

const EP = {
  border: '#d6cfc0',
  borderTertiary: '#ede8dc',
  text: '#3a2e1e',
  textSecondary: '#a89880',
  bgSecondary: '#fafaf7',
  bgPrimary: '#ffffff',
  focus: '#c0873a',
  focusRing: 'rgba(192, 135, 58, 0.12)',
  save: '#c0873a',
  saveHover: '#a87030',
  avatarBg: '#c0873a',
  avatarText: '#fff',
};

export interface ChaaviEditProfileModalProps {
  open: boolean;
  onClose: () => void;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  saving: boolean;
  error: string | null;
  onSave: () => void;
}

const inputSx = {
  width: '100%',
  height: 40,
  border: `0.5px solid ${EP.border}`,
  borderRadius: '8px',
  px: 1.5,
  fontSize: '0.875rem',
  color: EP.text,
  bgcolor: EP.bgSecondary,
  fontFamily: '"Jost", "Roboto", "Helvetica Neue", Arial, sans-serif',
  outline: 'none',
  boxSizing: 'border-box' as const,
  transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
  '&:focus': {
    borderColor: EP.focus,
    boxShadow: `0 0 0 3px ${EP.focusRing}`,
    bgcolor: EP.bgPrimary,
  },
  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
};

const labelSx = {
  fontSize: '0.6875rem',
  fontWeight: 500,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  color: EP.textSecondary,
  fontFamily: '"Jost", "Roboto", sans-serif',
};

function initials(first: string, last: string, email: string): string {
  const f = first?.trim().charAt(0);
  const l = last?.trim().charAt(0);
  if (f && l) return (f + l).toUpperCase();
  if (f) return (f + (first.trim().charAt(1) || f)).toUpperCase().slice(0, 2);
  const e = email?.trim().charAt(0);
  return e ? e.toUpperCase() + (email.trim().charAt(1) || e).toUpperCase().slice(0, 1) : '?';
}

const ChaaviEditProfileModal: React.FC<ChaaviEditProfileModalProps> = ({
  open,
  onClose,
  firstName,
  lastName,
  email,
  phoneNumber,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  saving,
  error,
  onSave,
}) => {
  const av = initials(firstName, lastName, email);

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '16px',
          width: '100%',
          maxWidth: 440,
          overflow: 'hidden',
          border: `0.5px solid ${EP.border}`,
          bgcolor: EP.bgPrimary,
          m: 2,
        },
      }}
      BackdropProps={{
        sx: { backgroundColor: 'rgba(0,0,0,0.45)' },
      }}
    >
      <Box sx={{ pt: 3, px: 3, pb: 0 }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            bgcolor: EP.avatarBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.125rem',
            fontWeight: 500,
            color: EP.avatarText,
            fontFamily: '"Jost", "Roboto", sans-serif',
            mb: 2,
          }}
        >
          {av}
        </Box>
        <Typography
          component="p"
          sx={{
            fontSize: '1.0625rem',
            fontWeight: 500,
            color: EP.text,
            fontFamily: '"Jost", "Roboto", sans-serif',
            m: 0,
            mb: 0.5,
          }}
        >
          Edit profile
        </Typography>
        <Typography
          component="p"
          sx={{
            fontSize: '0.8125rem',
            color: EP.textSecondary,
            fontFamily: '"Jost", "Roboto", sans-serif',
            m: 0,
            lineHeight: 1.4,
          }}
        >
          Update how your name and email appear in {APP_DISPLAY_NAME}.
        </Typography>
        <Box sx={{ height: '0.5px', bgcolor: EP.borderTertiary, mt: 2.5 }} />
      </Box>

      <Box
        component="form"
        id="chaavi-edit-profile-form"
        onSubmit={e => {
          e.preventDefault();
          onSave();
        }}
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        <Box sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.625 }}>
            <Typography component="label" htmlFor="ep-first" sx={labelSx}>
              First name
            </Typography>
            <Box
              id="ep-first"
              component="input"
              autoFocus
              disabled={saving}
              value={firstName}
              onChange={e => onFirstNameChange(e.target.value)}
              sx={inputSx}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.625 }}>
            <Typography component="label" htmlFor="ep-last" sx={labelSx}>
              Last name
            </Typography>
            <Box
              id="ep-last"
              component="input"
              disabled={saving}
              value={lastName}
              onChange={e => onLastNameChange(e.target.value)}
              sx={inputSx}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.625 }}>
          <Typography component="label" htmlFor="ep-email" sx={labelSx}>
            Email
          </Typography>
          <Box
            id="ep-email"
            component="input"
            type="email"
            disabled={saving}
            value={email}
            onChange={e => onEmailChange(e.target.value)}
            sx={{
              ...inputSx,
              ...(error ? { borderColor: '#c0503a', '&:focus': { borderColor: '#c0503a' } } : {}),
            }}
          />
          {error ? (
            <Typography sx={{ fontSize: '0.75rem', color: '#b03a2a', fontFamily: '"Jost", sans-serif' }}>
              {error}
            </Typography>
          ) : null}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: EP.bgSecondary,
            border: `0.5px solid ${EP.borderTertiary}`,
            borderRadius: '8px',
            px: 1.5,
            py: 1.25,
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 16 16"
            fill="none"
            sx={{ width: 16, height: 16, flexShrink: 0, color: EP.textSecondary }}
          >
            <rect x="3" y="7" width="10" height="8" rx="2" stroke="currentColor" strokeWidth={1.2} />
            <path
              d="M5 7V5a3 3 0 016 0v2"
              stroke="currentColor"
              strokeWidth={1.2}
              strokeLinecap="round"
            />
          </Box>
          <Typography
            sx={{
              fontSize: '0.8125rem',
              color: EP.textSecondary,
              fontFamily: '"Jost", "Roboto", sans-serif',
              lineHeight: 1.45,
            }}
          >
            Phone{' '}
            <Box component="span" sx={{ color: EP.text, fontWeight: 500 }}>
              {phoneNumber}
            </Box>{' '}
            is your login and cannot be changed here.
          </Typography>
        </Box>
        </Box>

        <Box
          sx={{
            px: 3,
            pb: 3,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Button
            type="button"
            onClick={onClose}
            disabled={saving}
            sx={{
              height: 36,
              px: 2,
              borderRadius: '8px',
              border: `0.5px solid ${EP.border}`,
              color: EP.textSecondary,
              fontSize: '0.875rem',
              textTransform: 'none',
              fontFamily: '"Jost", "Roboto", sans-serif',
              bgcolor: 'transparent',
              '&:hover': { bgcolor: EP.bgSecondary, border: `0.5px solid ${EP.border}` },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={saving}
            sx={{
              height: 36,
              px: 2.5,
              borderRadius: '8px',
              bgcolor: EP.save,
              color: '#fff',
              fontSize: '0.875rem',
              fontWeight: 500,
              textTransform: 'none',
              fontFamily: '"Jost", "Roboto", sans-serif',
              '&:hover': { bgcolor: EP.saveHover },
              '&:active': { transform: 'scale(0.98)' },
            }}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: '#fff' }} />
            ) : (
              'Save changes'
            )}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ChaaviEditProfileModal;
